import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import cn from 'classnames';
import { Popover } from 'antd';
import DownloadPreferencePopup from '$/components/DownloadPreferencePopup';
import IconFont from '$/components/IconFont';
import { message } from '$/components/Message';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { downloadImageUrl } from '$/utils/download';
import { handleImageUrl } from '$/utils/image';
import {
  getAigcWaterMarkSwitchByState,
  resolveNoBrandWatermarkDownloadUrlByApi,
} from '$/utils/noBrandWatermarkDownload';
import type { RoleGenFormValues } from '../RoleSettingForm';
import type { ThreeViewImage } from '../useRoleGenSubmit';
import {
  trackRoboneoAssetRoleHoverClick,
  trackRoboneoAssetRoleHoverShow,
} from '../../../tracking';
import styles from './index.module.scss';

const ROLE_HISTORY_THUMB_REQUEST_SIZE = 160;

/** 生成历史记录条目。 */
export interface RoleGenHistoryItem {
  taskId: string;
  createdAt: number;
  modelId: string;
  images: ThreeViewImage[];
  formSnapshot: RoleGenFormValues;
  displayName?: string;
  displayTime?: string;
  loading?: boolean;
  failed?: boolean;
}

interface HistoryListProps {
  items: RoleGenHistoryItem[];
  selectedTaskId?: string;
  scrollToTaskId?: string;
  roomId: string;
  /** 点击有结果的历史项复用为当前预览 */
  onPreview?: (item: RoleGenHistoryItem) => void;
  /** 点击重新编辑时回填左侧表单 */
  onEdit?: (item: RoleGenHistoryItem) => void;
  /** 将有结果的历史角色保存到资产库 */
  onSaveAsAsset?: (item: RoleGenHistoryItem) => void;
  /** 删除当前弹框内的历史项 */
  onDelete?: (item: RoleGenHistoryItem) => void;
}

/** 读取历史项埋点使用的首张结果图 URL。 */
function getRoleHistoryTrackMediaUrl(item: RoleGenHistoryItem): string {
  return item.images[0]?.url || '';
}

/** 读取历史项埋点使用的角色名，缺省时回退表单快照。 */
function getRoleHistoryTrackName(item: RoleGenHistoryItem): string {
  return item.displayName || item.formSnapshot.name.trim();
}

/**
 * 创建角色生成历史横向列表，支持点击预览、回填编辑和横向滚动。
 *
 * @param props 见 HistoryListProps
 * @returns 历史列表
 */
function HistoryList({
  items, selectedTaskId, scrollToTaskId, roomId, onPreview, onEdit, onSaveAsAsset, onDelete,
}: HistoryListProps): React.ReactElement {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const isOversea = useOfficialStore((state) => state.isOversea());
  const listRef = useRef<HTMLDivElement>(null);
  const scrollTargetCardRef = useRef<HTMLDivElement>(null);
  const locatedTaskIdRef = useRef('');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [openMenuTaskId, setOpenMenuTaskId] = useState<string | null>(null);

  /** 根据横向滚动位置更新左右箭头显隐，避免无溢出时展示无效按钮。 */
  const updateScrollButtons = useCallback((): void => {
    const list = listRef.current;
    if (!list) return;
    const maxScrollLeft = list.scrollWidth - list.clientWidth;
    setCanScrollLeft(list.scrollLeft > 1);
    setCanScrollRight(maxScrollLeft - list.scrollLeft > 1);
  }, []);

  /** 点击左右箭头时按一个可视区域滚动历史记录。 */
  const handleScrollHistory = useCallback((direction: 'left' | 'right'): void => {
    const list = listRef.current;
    if (!list) return;
    const distance = Math.max(list.clientWidth - 80, 80);
    list.scrollBy({
      left: direction === 'left' ? -distance : distance,
      behavior: 'smooth',
    });
  }, []);

  /** 下载历史角色的首张结果图，文件名优先使用角色名称，并上报菜单保存点击。 */
  const handleDownload = useCallback(async (item: RoleGenHistoryItem): Promise<void> => {
    const image = item.images[0];
    if (!image) return;
    trackRoboneoAssetRoleHoverClick({
      roomId,
      taskId: item.taskId,
      mediaUrl: image.originalUrl,
      roleName: getRoleHistoryTrackName(item),
      clickType: 'save',
    });
    setOpenMenuTaskId(null);
    try {
      let watermarkState: '0' | '1' = '0';
      if (!isOversea) {
        const preference = await DownloadPreferencePopup.show();
        if (preference === '2') return;
        watermarkState = preference;
      }
      const watermarkUrl = handleImageUrl(image.originalUrl, {
        showWatermark: true,
        needFormat: false,
      });
      const fallbackUrl = handleImageUrl(image.originalUrl, {
        showWatermark: watermarkState === '1',
        needFormat: false,
      });
      const downloadUrl = await resolveNoBrandWatermarkDownloadUrlByApi({
        url: image.originalUrl,
        watermarkUrl,
        aigcWaterMarkSwitch: getAigcWaterMarkSwitchByState(watermarkState),
        fallbackUrl,
      });
      await downloadImageUrl({
        url: downloadUrl,
        name: item.displayName || `role-${item.taskId}`,
      });
    } catch {
      message.error(languageStore.smartcatTranslationText((value) => value.assets_download_fail));
    }
  }, [isOversea, languageStore, roomId]);

  useEffect(() => {
    updateScrollButtons();
    const list = listRef.current;
    if (!list) return undefined;
    list.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      list.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [items.length, updateScrollButtons]);

  /** 外部重新编辑角色时，将对应历史卡片横向滚动到列表可视区域中央。 */
  useEffect(() => {
    if (!scrollToTaskId) {
      locatedTaskIdRef.current = '';
      return undefined;
    }
    if (locatedTaskIdRef.current === scrollToTaskId) return undefined;
    const animationFrame = window.requestAnimationFrame(() => {
      const list = listRef.current;
      const card = scrollTargetCardRef.current;
      if (!list || !card) return;
      const listRect = list.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const nextScrollLeft = list.scrollLeft
        + cardRect.left
        - listRect.left
        - (listRect.width - cardRect.width) / 2;
      list.scrollTo({ left: Math.max(0, nextScrollLeft), behavior: 'auto' });
      locatedTaskIdRef.current = scrollToTaskId;
      updateScrollButtons();
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [items, scrollToTaskId, updateScrollButtons]);

  if (items.length === 0) {
    return (
      <section className={styles.historySection}>
        <div className={styles.historyHeader}>
          <span>{languageStore.smartcatTranslationText((value) => value.character_created_)}</span>
        </div>
        <div className={styles.historyEmpty}>暂无历史生成记录</div>
      </section>
    );
  }

  return (
    <section className={styles.historySection}>
      <div className={styles.historyHeader}>
        <span>{languageStore.smartcatTranslationText((value) => value.character_created_)}</span>
      </div>
      <div className={styles.historyScroller}>
        <div className={styles.historyGrid} ref={listRef}>
          {items.map((item) => {
            const coverUrl = item.images[0]?.originalUrl || '';
            const selected = selectedTaskId === item.taskId;
            let thumbContent = (
              <IconFont
                name="fourPointedStarFill"
                fontSize={18}
                color="currentColor"
                className={styles.gradientStarIcon}
              />
            );
            if (item.loading) {
              thumbContent = <IconFont name="loadingCircleBold" fontSize={18} color="currentColor" className={styles.spinningIcon} />;
            } else if (coverUrl) {
              thumbContent = (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={handleImageUrl(coverUrl, {
                    size: ROLE_HISTORY_THUMB_REQUEST_SIZE,
                    showWatermark: !isOversea,
                  })}
                  alt=""
                  crossOrigin="anonymous"
                />
              );
            }

            return (
              <div
                ref={item.taskId === scrollToTaskId ? scrollTargetCardRef : undefined}
                className={cn(styles.historyCard, {
                  [styles.historyCardLoading]: item.loading,
                  [styles.historyCardSelected]: selected,
                })}
                key={item.taskId}
              >
                <button
                  type="button"
                  className={styles.historyCardMain}
                  onClick={() => onPreview?.(item)}
                >
                  <span className={styles.historyThumb}>{thumbContent}</span>
                  <span className={styles.historyInfo}>
                    <span>{item.displayName}</span>
                  </span>
                </button>
                {!item.loading && (
                  <Popover
                    trigger="click"
                    placement="rightTop"
                    arrowPointAtCenter={false}
                    overlayClassName={styles.historyMenuPopover}
                    zIndex={5700}
                    open={openMenuTaskId === item.taskId}
                    onOpenChange={(open) => {
                      setOpenMenuTaskId(open ? item.taskId : null);
                      if (open) {
                        trackRoboneoAssetRoleHoverShow({
                          roomId,
                          taskId: item.taskId,
                          mediaUrl: getRoleHistoryTrackMediaUrl(item),
                          roleName: getRoleHistoryTrackName(item),
                        });
                      }
                    }}
                    content={(
                      <div className={styles.historyMenu}>
                        <button
                          type="button"
                          className={styles.historyMenuItem}
                          disabled={!coverUrl}
                          onClick={() => {
                            trackRoboneoAssetRoleHoverClick({
                              roomId,
                              taskId: item.taskId,
                              mediaUrl: getRoleHistoryTrackMediaUrl(item),
                              roleName: getRoleHistoryTrackName(item),
                              clickType: 'save',
                            });
                            setOpenMenuTaskId(null);
                            onSaveAsAsset?.(item);
                          }}
                        >
                          <IconFont name="folderOpenAddBold" fontSize={16} color="currentColor" />
                          <span>{languageStore.smartcatTranslationText((value) => value.save_to_asset_library)}</span>
                        </button>
                        <button
                          type="button"
                          className={styles.historyMenuItem}
                          onClick={() => {
                            trackRoboneoAssetRoleHoverClick({
                              roomId,
                              taskId: item.taskId,
                              mediaUrl: getRoleHistoryTrackMediaUrl(item),
                              roleName: getRoleHistoryTrackName(item),
                              clickType: 'edit',
                            });
                            setOpenMenuTaskId(null);
                            onEdit?.(item);
                          }}
                        >
                          <IconFont name="penSquareBold" fontSize={16} color="currentColor" />
                          <span>{languageStore.smartcatTranslationText((value) => value.re_edit)}</span>
                        </button>
                        <button
                          type="button"
                          className={styles.historyMenuItem}
                          onClick={() => {
                            trackRoboneoAssetRoleHoverClick({
                              roomId,
                              taskId: item.taskId,
                              mediaUrl: getRoleHistoryTrackMediaUrl(item),
                              roleName: getRoleHistoryTrackName(item),
                              clickType: 'delete',
                            });
                            setOpenMenuTaskId(null);
                            onDelete?.(item);
                          }}
                        >
                          <IconFont name="trashCanV2Bold" fontSize={16} color="currentColor" />
                          <span>{languageStore.smartcatTranslationText((value) => value.RoboNeo_Web_studio_delete)}</span>
                        </button>
                        <button
                          type="button"
                          className={styles.historyMenuItem}
                          disabled={!coverUrl}
                          onClick={() => handleDownload(item)}
                        >
                          <IconFont name="downloadBold" fontSize={16} color="currentColor" />
                          <span>{languageStore.smartcatTranslationText((value) => value.download)}</span>
                        </button>
                      </div>
                    )}
                  >
                    <button
                      type="button"
                      className={cn(styles.historyActionButton, {
                        [styles.historyActionButtonActive]: openMenuTaskId === item.taskId,
                      })}
                    >
                      <IconFont
                        name="ellipsisFill"
                        fontSize={12}
                        color="var(--content-staticLightPrimary, #FFF)"
                      />
                    </button>
                  </Popover>
                )}
              </div>
            );
          })}
        </div>
        {canScrollLeft && (
          <button type="button" className={cn(styles.historyScrollButton, styles.historyScrollButtonLeft)} onClick={() => handleScrollHistory('left')}>
            <IconFont name="arrowLeftBold" fontSize={16} color="currentColor" />
          </button>
        )}
        {canScrollRight && (
          <button type="button" className={cn(styles.historyScrollButton, styles.historyScrollButtonRight)} onClick={() => handleScrollHistory('right')}>
            <IconFont name="arrowRightBold" fontSize={16} color="currentColor" />
          </button>
        )}
      </div>
    </section>
  );
}

export default HistoryList;
